// Marge de contribution par vente (avant frais fixes) sur Amazon (FBA), sur le site et en B2B.
// Conventions :
// - les frais sont comptés hors taxes : pour une entreprise assujettie, la TVA sur les frais se récupère ;
// - la commission Amazon et les frais de paiement s'appliquent au prix TTC payé par le client ;
// - la publicité (TACoS) se lit comme dans Seller Central et Shopify : dépense HT ÷ ventes TTC ;
// - les retours sont rapportés aux ventes conservées : taux ÷ (1 − taux) × coût d'un retour ;
// - le B2B se vend hors taxes, sans publicité.
import { num } from './stockSecurite';

// Exemple fictif : la Veste Altitude de la démo de l'accueil (mêmes frais sur Amazon, au centime).
export const MARGE_EXEMPLE = {
  achat: '22.00',
  transportIn: '2.20',
  tva: '20',
  amazon: { prix: '106.80', remise: '0', commission: '15', fba: '7.40', envoi: '0.50', stockage: '0.20', mois: '2', pub: '8', retours: '4', coutRetour: '28.80' },
  site: { prix: '99.00', remise: '5', paiementPct: '1.8', paiementFixe: '0.25', unites: '1.1', expedition: '6.90', preparation: '1.50', stockage: '0.30', pub: '12', retours: '3', coutRetour: '28.80' },
  b2b: { prix: '52.00', remise: '3', commission: '8', transport: '1.20', retours: '1', coutRetour: '30' }
};

export const CANAUX = [
  ['amazon', 'Amazon'],
  ['site', 'votre site'],
  ['b2b', 'le B2B']
];

const pos = (v) => Math.max(0, num(v) || 0);
const pct = (v) => Math.min(100, pos(v)) / 100;
// Coût des retours par vente conservée (le taux est plafonné à 90 % pour rester calculable).
const retoursPar = (taux, cout) => {
  const t = Math.min(0.9, pct(taux));
  return (t / (1 - t)) * pos(cout);
};

// Une vente sur un canal : lignes de coût par unité, marges et seuil de publicité.
export function margeCanal(canal, c, commun) {
  const produit = pos(commun.achat) + pos(commun.transportIn);
  const tva = 1 + pct(commun.tva);
  let prixTTC = null;
  let prixHT;
  let frais;
  let pubBase;
  if (canal === 'b2b') {
    prixHT = pos(c.prix) * (1 - pct(c.remise));
    frais = [
      ['Commission d\'agent', prixHT * pct(c.commission)],
      ['Transport', pos(c.transport)]
    ];
    pubBase = 0;
  } else {
    prixTTC = pos(c.prix) * (1 - pct(c.remise));
    prixHT = prixTTC / tva;
    if (canal === 'amazon') {
      frais = [
        ['Commission Amazon', prixTTC * pct(c.commission)],
        ['Expédition FBA', pos(c.fba)],
        ['Envoi vers Amazon', pos(c.envoi)],
        ['Stockage Amazon', pos(c.stockage) * pos(c.mois)]
      ];
    } else {
      const unites = Math.max(1, num(c.unites) || 1);
      frais = [
        ['Frais de paiement', prixTTC * pct(c.paiementPct) + pos(c.paiementFixe) / unites],
        ['Expédition', pos(c.expedition) / unites],
        ['Préparation', pos(c.preparation) / unites],
        ['Stockage', pos(c.stockage)]
      ];
    }
    pubBase = prixTTC;
  }
  if (!(prixHT > 0)) return { error: 'Indiquez un prix de vente.' };
  const fraisCanal = frais.reduce((a, [, v]) => a + v, 0);
  const retours = retoursPar(c.retours, c.coutRetour);
  const avantPub = prixHT - produit - fraisCanal - retours;
  const pub = pubBase * pct(c.pub);
  const marge = avantPub - pub;
  return {
    canal,
    prixTTC,
    prixHT,
    produit,
    frais,
    fraisCanal,
    retours,
    avantPub,
    pub,
    marge,
    taux: marge / prixHT,
    // TACoS maximal avant de vendre à perte, sur la même base que Seller Central (ventes TTC).
    pubMax: pubBase > 0 ? Math.max(0, avantPub / pubBase) : null,
    pubTaux: pct(c.pub)
  };
}

export const marges = (p) => Object.fromEntries(CANAUX.map(([k]) => [k, margeCanal(k, p[k] || {}, p)]));
